import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../Button/Button';
import { SparkIcon } from '../SparkIcon';
import { ValidationCard, type ValidationCardProps } from './ValidationCard';
import './Widgets.css';

/** Horizontally scrollable row of ValidationCards with left/right navigation arrows. */
export type ValidationCardCarouselProps = {
  items: ValidationCardProps[];
  /** Class applied to every ValidationCard in the track (e.g. to enforce a uniform width). */
  cardClassName?: string;
  className?: string;
  /** Card index to bring into view when `scrollIntoViewNonce` changes (0-based in `items`). */
  scrollIntoViewIndex?: number | null;
  /** Increment to re-run scroll even when the index is unchanged. */
  scrollIntoViewNonce?: number;
};

export function ValidationCardCarousel({
  items,
  cardClassName,
  className = '',
  scrollIntoViewIndex = null,
  scrollIntoViewNonce = 0,
}: ValidationCardCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkOverflow, { passive: true });
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', checkOverflow);
      ro.disconnect();
    };
  }, [checkOverflow]);

  useEffect(() => {
    if (!scrollIntoViewNonce || scrollIntoViewIndex == null || scrollIntoViewIndex < 0) return undefined;
    const track = scrollRef.current;
    const card = track?.children?.[scrollIntoViewIndex];
    if (card instanceof HTMLElement) {
      card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    return undefined;
  }, [scrollIntoViewNonce, scrollIntoViewIndex]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.75;
    el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
  };

  const rootClasses = ['spark-validation-card-carousel', className].filter(Boolean).join(' ');

  return (
    <div className={rootClasses}>
      {showLeft && (
        <Button
          variant="ghost"
          size="small"
          type="button"
          className="spark-validation-card-carousel-chevron spark-validation-card-carousel-chevron-left"
          onClick={() => scroll('left')}
          aria-label="Scroll left"
          startIcon={<SparkIcon slug="chevron-left" size="small" />}
        />
      )}
      <div ref={scrollRef} className="spark-validation-card-carousel-track">
        {items.map((item, i) => (
          <ValidationCard key={i} {...item} className={[item.className, cardClassName].filter(Boolean).join(' ')} />
        ))}
      </div>
      {showRight && (
        <Button
          variant="ghost"
          size="small"
          type="button"
          className="spark-validation-card-carousel-chevron spark-validation-card-carousel-chevron-right"
          onClick={() => scroll('right')}
          aria-label="Scroll right"
          startIcon={<SparkIcon slug="chevron-right" size="small" />}
        />
      )}
    </div>
  );
}
